/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {firstValueFrom, tap} from 'rxjs';

import {PERMISSIONS} from '../../../components/auth/auth-constants';
import {UserProfileSaverService} from '../../../components/session/user-profile-saver.service';
import {SignalAdminResourceContextActionsComponent} from '../../common/controller/signal-admin-resource-context-actions.component';
import {SignalAdminResourcePageState} from '../../common/controller/signal-admin-resource-page-state';
import {SignalAdminResourceTitleComponent} from '../../common/controller/signal-admin-resource-title.component';
import {ListDefaultActionsComponent} from '../../common/directives/list-default-actions.component';
import {stateTitleView} from '../../common/directives/html/state-title.component';
import {AdnRouterHelperApi, StateDeclarationBuilder} from '../../routing/state-utils';
import {TeamGroupApiService, TeamGroupModel} from './team-group-api.service';
import {TeamGroupItemPageComponent} from './team-group-item-page.component';
import {TeamGroupRoutePageComponent} from './team-group-route-page.component';

export function teamGroupStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  const states = new StateDeclarationBuilder();
  const api = adnRouterHelper.services.service(TeamGroupApiService);
  const userProfileSaver = adnRouterHelper.services.service(UserProfileSaverService);
  const permissions = adnRouterHelper.services.localUserPermissions();
  const readOnly = () => !permissions.hasNetwork(PERMISSIONS.manageSystem.apiKey)
    && !permissions.hasDataNetworkPermission(PERMISSIONS.dataAdmin.apiKey)
    && !permissions.hasNetwork(PERMISSIONS.axAdvertiserAdmin.apiKey);
  const rh = adnRouterHelper.createRouterHelper('team-groups.team-group', TeamGroupApiService, 'app.adm', 'app/admin');

  states.add(rh.listPath(), rh.list({
    data: {app: ['ADVERTISING', 'DATA']},
    resolve: {listReadOnly: readOnly},
    views: {
      'content@app': {component: TeamGroupRoutePageComponent, bindings: {readOnly: 'listReadOnly'}},
      'context@app': {component: ListDefaultActionsComponent, bindings: {readOnly: 'listReadOnly', newStateName: 'listNewStateName'}},
      'title@app': stateTitleView()
    }
  }));
  states.add(rh.newItemPath(), rh.newItem());
  states.add(rh.itemPath(), rh.item({
    data: {app: ['ADVERTISING', 'DATA']},
    resolve: {
      teamGroupPage: function($stateParams: any) {
        if ($stateParams.isNew) {
          return new SignalAdminResourcePageState<TeamGroupModel>(
            {id: $stateParams.id, objectState: 'ACTIVE', validationWarnings: []}, true, readOnly(), api, 'Team Group'
          );
        }
        return firstValueFrom(api.get($stateParams.id).pipe(
          tap((model) => userProfileSaver.saveObj(model, 'adnLastViewed'))
        )).then((model) => new SignalAdminResourcePageState(model, false, readOnly(), api, 'Team Group'));
      },
      model: function(teamGroupPage: SignalAdminResourcePageState<TeamGroupModel>) {
        return teamGroupPage.model();
      }
    },
    views: {
      'content@app': {component: TeamGroupItemPageComponent, bindings: {state: 'teamGroupPage'}},
      'context@app': {component: SignalAdminResourceContextActionsComponent, bindings: {state: 'teamGroupPage'}},
      'title@app': {component: SignalAdminResourceTitleComponent, bindings: {state: 'teamGroupPage'}}
    }
  }));
  return states.build();
}
